/**
 * 툴팁 컴포넌트
 * 사이드바 collapsed 상태에서 메뉴 텍스트를 표시하는 툴팁
 */

import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, spacing, borderRadius, shadows, animations, zIndex } from '../../styles/designTokens';

// ===== 타입 정의 =====
export interface TooltipProps {
    content: string;
    children: React.ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right';
    delay?: number;
    disabled?: boolean;
    className?: string;
}

// ===== 스타일드 컴포넌트 =====
const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipContent = styled.div<{
    $position: 'top' | 'bottom' | 'left' | 'right';
    $visible: boolean;
}>`
  position: absolute;
  z-index: ${zIndex.tooltip};
  padding: ${spacing[2]} ${spacing[3]};
  background: ${colors.semantic.text.primary};
  color: ${colors.semantic.text.inverse};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};
  white-space: nowrap;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  pointer-events: none;
  
  /* 화살표 */
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border: 6px solid transparent;
  }
  
  /* 위치별 스타일 */
  ${({ $position }) => {
        switch ($position) {
            case 'top':
                return css`
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(-8px);
          
          &::before {
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: ${colors.semantic.text.primary};
          }
        `;
            case 'bottom':
                return css`
          top: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(8px);
          
          &::before {
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: ${colors.semantic.text.primary};
          }
        `;
            case 'left':
                return css`
          right: 100%;
          top: 50%;
          transform: translateY(-50%) translateX(-8px);
          
          &::before {
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-left-color: ${colors.semantic.text.primary};
          }
        `;
            case 'right':
                return css`
          left: 100%;
          top: 50%;
          transform: translateY(-50%) translateX(8px);
          
          &::before {
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-right-color: ${colors.semantic.text.primary};
          }
        `;
        }
    }}
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

// ===== 메인 컴포넌트 =====
export const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    position = 'right',
    delay = 300,
    disabled = false,
    className
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const showTooltip = () => {
        if (disabled) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };

    const hideTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <TooltipContainer
            ref={containerRef}
            className={className}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
        >
            {children}
            <TooltipContent
                $position={position}
                $visible={isVisible}
                role="tooltip"
                aria-hidden={!isVisible}
            >
                {content}
            </TooltipContent>
        </TooltipContainer>
    );
};

export default Tooltip;
